const FILTERED_VALUE = '[Filtered]';
const CIRCULAR_VALUE = '[Circular]';
const TRUNCATED_VALUE = '[Truncated]';
const UNSERIALIZABLE_VALUE = '[Unserializable]';
const MAX_DEPTH = 10;
const MAX_BREADTH = 1000;
const MAX_VALUES = 10000;

const SENSITIVE_KEYS = new Set([
  'accesstoken',
  'authorization',
  'confirmationcode',
  'password',
  'refreshtoken',
  'session',
]);

/**
 * Creates a bounded sanitizer for one Sentry event.
 * @returns {(value: *, depth?: number) => *}
 */
const createSanitizer = () => {
  const seen = new WeakSet();
  let remaining = MAX_VALUES;

  /**
   * @param {*} value
   * @param {number} depth
   * @returns {*}
   */
  const sanitizeValue = (value, depth = 0) => {
    if (remaining <= 0) {
      return TRUNCATED_VALUE;
    }

    remaining -= 1;

    if (value === null || typeof value !== 'object') {
      return value;
    }

    if (depth >= MAX_DEPTH) {
      return TRUNCATED_VALUE;
    }

    if (seen.has(value)) {
      return CIRCULAR_VALUE;
    }
    seen.add(value);

    if (typeof value.toJSON === 'function') {
      try {
        return sanitizeValue(value.toJSON(), depth + 1);
      } catch {
        return UNSERIALIZABLE_VALUE;
      }
    }

    const entries = Array.isArray(value)
      ? value.slice(0, MAX_BREADTH).map((nestedValue, index) => [index, nestedValue])
      : Object.entries(value).slice(0, MAX_BREADTH);
    /** @type {Record<string, *> | Array<*>} */
    const sanitizedValue = Array.isArray(value) ? [] : {};

    for (let index = 0; index < entries.length; index += 1) {
      if (remaining <= 0) {
        break;
      }
      const [key, nestedValue] = entries[index];
      const sanitizedNestedValue = SENSITIVE_KEYS.has(String(key).toLowerCase())
        ? FILTERED_VALUE
        : sanitizeValue(nestedValue, depth + 1);
      if (Array.isArray(sanitizedValue)) {
        sanitizedValue.push(sanitizedNestedValue);
      } else {
        sanitizedValue[String(key)] = sanitizedNestedValue;
      }
    }

    return sanitizedValue;
  };

  return sanitizeValue;
};

/**
 * @param {*} event
 * @returns {*}
 */
export const sanitizeSentryEvent = (event) => createSanitizer()(event);

/**
 * @param {Record<string, *> | null} state
 * @returns {Record<string, *> | null}
 */
export const removeAuthenticationState = (state) => {
  if (state === null || typeof state !== 'object') {
    return state;
  }

  const stateWithoutAuthentication = { ...state };
  delete stateWithoutAuthentication.auth;
  return stateWithoutAuthentication;
};
